import { ItemModalStyled } from '@OnArk/styles/BuilderItems.styled';
import { ButtonStyled } from '@OnArk/styles/Buttons.styled';
import { SectionSidebarItemStyled } from '@OnArk/styles/CondensableSidebar.styled';
import {
  InputStyled,
  LabelStyled,
  ToggleLabelStyled,
  ToggleStyled,
} from '@OnArk/styles/Form.styled';

import React from 'react';

import { useRouter } from 'next/router';

import { Input } from '@hangar31/rc-form';
import { MaxWidthStyled } from '@hangar31/rc-layout';
import useModal, { WithModalProps } from '@hangar31/rc-modal';
import Footer from '@OnArk/admin/Course/BuilderItems/Helpers/Footer';
import { WALKTHROUGH_ENABLED } from '@OnArk/admin/GettingStarted/enabled';
import ModalTour from '@OnArk/admin/GettingStarted/ModalTour';
import { modalTours } from '@OnArk/admin/GettingStarted/tours';
import { useSection } from '@OnArk/contexts/section';
import EditorInput from '@OnArk/global/EditorInput';
import Icon from '@OnArk/SVGs/Icon';
import { offerPlanIfWalled } from '@OnArk/utils/buildingWall';
import posthog from '@OnArk/utils/posthog';

import CourseLink from '../../Course/CourseLink';

function ClassSettingsForm({ modal }: WithModalProps) {
  const { church, client, course, setChanges } = useSection();
  const router = useRouter();
  // Duplicate and Delete sit inside the settings <form>. A <button> defaults to
  // type="submit", so without an explicit type a click ALSO submitted the form:
  // the delete request went out, then the browser's default action sent
  // updateCourse for the course that was just deleted. Whichever landed second
  // failed — Rollbar onark-api #128, every occurrence at the exact second a
  // course was removed. type="button" keeps these clicks out of the form.
  const deleteCourse = async () => {
    if (confirm('Are you sure you want to delete this class?')) {
      try {
        await client.deleteCourse({ id: course.id });
      } catch (err) {
        if (offerPlanIfWalled(err, router.push)) return;
        throw err;
      }
      posthog?.capture('deleteCourse');
      void router.push('/admin/classes');
    }
  };
  const duplicateCourse = async () => {
    try {
      const { data } = await client.duplicateCourse({ id: course.id });

      posthog?.capture('duplicateCourse');
      void router.push(`/admin/classes/${data.id}`);
    } catch (err) {
      if (!offerPlanIfWalled(err, router.push)) throw err;
    }
  };

  return (
    <ItemModalStyled
      defaultData={course}
      onSubmit={async (_, __, changed) => {
        delete changed.changed;
        let data;

        try {
          ({ data } = await client.updateCourse({
            data: changed as CourseUpdateInput,
            id: course.id,
          }));
        } catch (err) {
          if (offerPlanIfWalled(err, router.push)) return course;
          throw err;
        }

        setChanges(c => c + 1);
        modal?.close();
        posthog?.capture('saveCourseSettings');

        // `data` is null when the course was deleted out from under this save
        // (another tab, or the delete/submit race above). There is nothing to
        // show for a course that no longer exists; keep the form's current
        // values rather than feeding it a null.
        return data ?? course;
      }}
    >
      <header> Class Settings</header>
      <main>
        <MaxWidthStyled grid>
          <LabelStyled data-tour="class-settings-name">
            <span>Class Title</span>
            <InputStyled required name="name" />
          </LabelStyled>
          <div data-tour="class-settings-url">
            <CourseLink churchUrl={church?.url} />
          </div>
          <LabelStyled data-tour="class-settings-description">
            <span>Course Description</span>
            <EditorInput
              css={{ '.ql-editor': { minHeight: '15rem' } }}
              modules={{
                toolbar: ['bold', 'italic', 'underline'],
              }}
              name="description"
              size="small"
            />
          </LabelStyled>
        </MaxWidthStyled>
      </main>
      <Footer>
        <MaxWidthStyled grid cols={3} data-tour="class-settings-actions">
          <ButtonStyled
            outline
            withIcon
            css={{ width: '100%' }}
            type="button"
            onClick={() => void duplicateCourse()}
          >
            <Icon name="ACTIONS_Duplicate" size="20" />
            Duplicate Class
          </ButtonStyled>

          <ButtonStyled
            negative
            outline
            withIcon
            css={{ width: '100%' }}
            type="button"
            onClick={() => void deleteCourse()}
          >
            <Icon name="ACTIONS_Delete" size="20" />
            Delete Class
          </ButtonStyled>
          <ToggleLabelStyled
            css={{ margin: '0 2rem 0 auto' }}
            data-tour="class-settings-public"
          >
            <ToggleStyled as={Input} name="public" type="checkbox" />
            Public
          </ToggleLabelStyled>
        </MaxWidthStyled>
      </Footer>
      {WALKTHROUGH_ENABLED && (
        <ModalTour name="classSettings" steps={modalTours.classSettings} />
      )}
    </ItemModalStyled>
  );
}

function Button({ modal, ...props }: WithModalProps) {
  return (
    <SectionSidebarItemStyled
      data-active={modal?.isOpen}
      data-tour="section-class-settings"
      {...props}
      as="button"
      css={{ width: '100%' }}
    >
      <Icon
        left="0.8"
        name="ACTIONS_Settings"
        position="right"
        right="0.8"
        size="30"
        tooltip="Class Settings"
      />
      <span>Class Settings</span>
    </SectionSidebarItemStyled>
  );
}

export default function ClassSettings() {
  const [Modal] = useModal({
    ButtonComponent: Button,
    maxHeight: 700,
    maxWidth: 700,
    ModalComponent: ClassSettingsForm,
    ms: 300,
    transitionOpacity: true,
  });

  return <Modal />;
}
